import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Field, Label } from "@headlessui/react";
import { ColumnGroup } from "primereact/columngroup";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Row } from "primereact/row";

import { getParamAsInt } from "../../../../app/utils";
import { formatMinutes } from "../../../../app/datetimeUtils";
import { useAppSelector } from "../../../../app/hooks";
import { CustomSortIcon } from "../../../../components/DatatableHelpers";
import FormattedDateAndTime from "../../../../components/FormattedDateAndTime";
import { SelectList } from "../../../../components/input/SelectList";
import { DownloadButton } from "../../shared/DownloadButton";
import { LessonUsageByLessonChart } from "./LessonUsageByLessonChart";
import { TutorialUsage } from "./TutorialUsageByCourseAndTutorialViewModel";
import { useTutorialUsageByCourseAndTutorialQuery } from "./courseUsageReportApiSlice";

export default function LessonUsageByLessonReport() {
	const [queryParams] = useSearchParams();
	const courseId = getParamAsInt(queryParams, "courseId");
	const initialCategoryId = getParamAsInt(queryParams, "categoryId") ?? 0;
	const initialLessonId = getParamAsInt(queryParams, "lessonId") ?? 0;
	const { aspUrl } = useAppSelector((state) => state.site);

	const [categoryId, setCategoryId] = useState(initialCategoryId);
	const [lessonId, setLessonId] = useState(initialLessonId);

	const result = useTutorialUsageByCourseAndTutorialQuery({
		courseId: courseId,
		lessonId: lessonId,
		focusId: categoryId,
	});
	const data = result.data;

	const handleCategoryChange = (categoryId: string) => {
		setCategoryId(Number(categoryId));
	};

	const handleLessonChange = (lessonId: string) => {
		setLessonId(Number(lessonId));
	};

	return (
		<main>
			<div className="flex justify-between">
				<div className="w-3/4">
					<h1>Lesson Usage by Specific Lesson</h1>
				</div>
			</div>

			<p className="pt-8">
				The report below shows the most recent date of use and the total time spent using
				the lessons within the chosen category
			</p>
			{data && (
				<>
					<div className="flex justify-between items-end mt-4">
						<div>
							<Field className="w-full mt-4">
								<Label
									htmlFor="category-list"
									className="block font-semibold text-sm text-gray-900">
									Category
								</Label>
								<SelectList
									id="category-list"
									className="w-full mt-1"
									selectListItems={data.allFoci}
									value={categoryId.toString()}
									onChange={handleCategoryChange}
								/>
							</Field>
							<Field className="w-full mt-4">
								<Label
									htmlFor="lesson-list"
									className="block font-semibold text-sm text-gray-900">
									Lesson
								</Label>
								<SelectList
									id="lesson-list"
									className="w-full mt-1"
									selectListItems={data.allLessons}
									value={lessonId.toString()}
									onChange={handleLessonChange}
								/>
							</Field>
						</div>
						<div>
							<DownloadButton
								url={`${aspUrl}/Report/DownloadTutorialUsageFilteredByCourseAndFocusAndTutorial?courseId=${data.courseId}&categoryId=${categoryId}&tutorialId=${lessonId}`}
								disabled={data.tutorials.length == 0}
							/>
						</div>
					</div>

					<LessonUsageByLessonTable
						className="w-full mt-4"
						tutorialUsage={data.tutorials}
						tutorialUsageForCourse={data.tutorialUsageForCourse}
					/>

					{data.tutorials.length > 0 && (
						<LessonUsageByLessonChart className="mt-4" data={data.tutorials} />
					)}
				</>
			)}
		</main>
	);
}

function LessonUsageByLessonTable(props: {
	className?: string;
	tutorialUsage: TutorialUsage[];
	tutorialUsageForCourse: TutorialUsage;
}) {
	const hasData = props.tutorialUsage && props.tutorialUsage.length > 0;

	const lastUsedBodyTemplate = (rowData: TutorialUsage) => {
		return <FormattedDateAndTime date={rowData.lastUsedDate} />;
	};

	const timeSpentBodyTemplate = (rowData: TutorialUsage) => {
		return formatMinutes(rowData.timeSpentMinutes);
	};

	const footerGroup = (
		<ColumnGroup>
			<Row>
				<Column footer="Total Time" colSpan={2} footerClassName="font-medium !text-right" />
				<Column
					footer={formatMinutes(props.tutorialUsageForCourse.timeSpentMinutes)}
					footerClassName="font-medium"
				/>
			</Row>
		</ColumnGroup>
	);

	return (
		<DataTable
			value={props.tutorialUsage}
			dataKey="writerName"
			loading={props.tutorialUsage == undefined}
			emptyMessage="No results found"
			stripedRows
			sortIcon={CustomSortIcon}
			className={`block tbl tbl-data tbl-datatable min-w-[40rem] cursor-default ${props.className}`}
			footerColumnGroup={hasData && footerGroup}>
			<Column
				header="Student Name"
				field="writerName"
				sortable
				sortField="writerName"></Column>
			<Column
				header="Most Recent Use"
				body={lastUsedBodyTemplate}
				sortable
				sortField="lastUsedDate"></Column>
			<Column
				header="Total Time Spent"
				body={timeSpentBodyTemplate}
				sortable
				sortField="timeSpentMinutes"></Column>
		</DataTable>
	);
}
